// Клиент проверки цен. Как распознавание и магия, ходит только к своему
// серверу: ключи агрегаторов в браузер не попадают, наружу ходит сервер.
import { request } from '../auth/request';
import type { ApiResult } from '../auth/request';
import type { CurrencyDef } from '../types';
import type {
  PriceApplyInput,
  PriceCheck,
  PriceCheckRequest,
  PriceCheckResponse,
  PriceConfig,
  PriceFlightInput,
} from './types';

/**
 * Адрес поездки в запросе — тот же приём, что у состояния и снимков
 * (src/sync/api.ts): владельца сервер сверяет по сессии, поэтому чужой id даёт
 * 404, а не доступ к чужим данным. Без параметра открывается поездка по
 * умолчанию.
 */
function withTrip(path: string, tripId?: string | null): string {
  if (!tripId) return path;
  return `${path}${path.includes('?') ? '&' : '?'}trip=${encodeURIComponent(tripId)}`;
}

/** Конфиг фичи. 401 у гостя — штатный ответ, а не ошибка. */
export function fetchPriceConfig(signal?: AbortSignal): Promise<ApiResult<PriceConfig>> {
  return request<PriceConfig>('/api/prices/config', { method: 'GET', signal });
}

/** Последняя проверка (kind: 'check') по каждому рейсу поездки. */
export function fetchPriceChecks(
  tripId?: string | null,
  signal?: AbortSignal,
): Promise<ApiResult<{ checks: PriceCheck[] }>> {
  return request<{ checks: PriceCheck[] }>(withTrip('/api/prices', tripId), {
    method: 'GET',
    signal,
  });
}

/** Вся история одного рейса, новые сверху: и проверки, и отметки «поставили». */
export function fetchPriceHistory(
  segmentId: string,
  tripId?: string | null,
  signal?: AbortSignal,
): Promise<ApiResult<{ checks: PriceCheck[] }>> {
  const path = `${withTrip('/api/prices/history', tripId)}${tripId ? '&' : '?'}segment=${encodeURIComponent(segmentId)}`;
  return request<{ checks: PriceCheck[] }>(path, { method: 'GET', signal });
}

/**
 * Сама проверка. Справочник валют уходит вместе с рейсами: сервер приводит
 * найденные цены к валюте блока, а курсы живут у пользователя, не у сервера.
 */
export function checkPrices(
  flights: readonly PriceFlightInput[],
  currencies: readonly CurrencyDef[],
  tripId?: string | null,
  signal?: AbortSignal,
): Promise<ApiResult<PriceCheckResponse>> {
  const body: PriceCheckRequest = {
    flights: flights.slice(),
    currencies: currencies.slice(),
  };
  return request<PriceCheckResponse>(withTrip('/api/prices/check', tripId), {
    method: 'POST',
    json: body,
    signal,
  });
}

/**
 * Отметка в истории: такую-то цену поставили в блок. Цену блока этот запрос не
 * меняет и менять не может — состояние поездки живёт отдельно (см. README).
 */
export function applyPriceQuote(
  input: PriceApplyInput,
  tripId?: string | null,
  signal?: AbortSignal,
): Promise<ApiResult<{ check: PriceCheck }>> {
  return request<{ check: PriceCheck }>(withTrip('/api/prices/apply', tripId), {
    method: 'POST',
    json: input,
    signal,
  });
}
